import React, { useEffect, useState } from "react";
import {
  AuditRow,
  ensureAdminName,
  getAudit,
  PatientDetail,
  removeShare,
  setProvider,
  setTags,
  setTestFlag,
} from "./adminApi";
import { formatWhen } from "./adminShell";
import styles from "./AccountTab.module.css";

const ACTION_LABELS: Record<string, string> = {
  provider: "Provider access",
  tags: "Tags",
  "test-flag": "Test account",
  "remove-share": "Removed share",
};

function formatAddress(address: PatientDetail["profile"]["address"]): string {
  if (!address) return "Not given";
  if (typeof address === "string") return address;
  const parts = [address.line1, address.line2, address.city, address.state, address.postalCode, address.country];
  return parts.filter(Boolean).join(", ") || "Not given";
}

function auditDetail(row: AuditRow): string {
  const d = row.detail || {};
  if (row.action === "provider") return d.grant ? "made Provider" : "removed Provider";
  if (row.action === "test-flag") return d.isTest ? "marked as test account" : "unmarked as test account";
  if (row.action === "tags") return Array.isArray(d.tags) && d.tags.length ? `set to ${d.tags.join(", ")}` : "cleared";
  if (row.action === "remove-share") return `stopped sharing with ${String(d.viewer || "")}`;
  return "";
}

interface Props {
  patient: PatientDetail;
  onChange: (patient: PatientDetail) => void;
}

/** Admin-only account details and actions for one patient. Every change is audited server-side. */
export default function AccountTab({ patient, onChange }: Props) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const [tagText, setTagText] = useState(patient.tags.join(", "));
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [auditKey, setAuditKey] = useState(0);

  useEffect(() => setTagText(patient.tags.join(", ")), [patient.tags]);

  useEffect(() => {
    getAudit(200)
      .then((rows) => setAudit(rows.filter((r) => r.target === patient.email)))
      .catch(() => setAudit([]));
  }, [patient.email, auditKey]);

  const isProvider = patient.groups.includes("Provider");
  const p = patient.profile;

  /** Name first (it goes in the audit trail), then confirm, then the write. */
  async function act(confirmText: string | null, write: () => Promise<PatientDetail>, done: string) {
    if (!ensureAdminName()) {
      setNotice({ kind: "error", text: "Enter your name first: it is recorded with every change." });
      return;
    }
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    setNotice(null);
    try {
      onChange(await write());
      setNotice({ kind: "success", text: done });
      setAuditKey((k) => k + 1);
    } catch (e) {
      setNotice({ kind: "error", text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  const toggleProvider = () =>
    act(
      isProvider
        ? `Remove Provider access from ${patient.email}? They stop seeing shared patients once their current login expires (up to an hour).`
        : `Make ${patient.email} a Provider? They will be able to see every patient who shares with them.`,
      async () => ({ ...patient, groups: await setProvider(patient.email, !isProvider) }),
      isProvider ? "Provider access removed." : "Provider access granted. It applies the next time they sign in."
    );

  const saveTags = () =>
    act(
      null,
      async () => ({ ...patient, tags: await setTags(patient.email, tagText.split(",")) }),
      "Tags saved."
    );

  const toggleTest = () =>
    act(
      null,
      async () => ({ ...patient, isTest: await setTestFlag(patient.email, !patient.isTest) }),
      patient.isTest ? "No longer a test account." : "Marked as a test account: hidden from overview counts."
    );

  const unshare = (viewer: string) =>
    act(
      `Stop ${patient.email} sharing their data with ${viewer}?`,
      async () => ({ ...patient, sharingWith: await removeShare(patient.email, viewer) }),
      `${viewer} can no longer see this patient.`
    );

  const age = typeof p.birthYear === "number" ? new Date().getFullYear() - p.birthYear : null;

  return (
    <div className={styles.grid}>
      {notice && (
        <p className={`ui-banner ui-banner--${notice.kind} ${styles.wide}`} role={notice.kind === "error" ? "alert" : "status"}>
          {notice.text}
        </p>
      )}

      <section className="ui-card">
        <h2 className={styles.heading}>Profile</h2>
        <dl className={styles.facts}>
          <dt>Name</dt>
          <dd>{[p.givenName, p.familyName].filter(Boolean).join(" ") || "Not given"}</dd>
          <dt>Age</dt>
          <dd>{age != null ? `${age} (born ${p.birthYear})` : "Not given"}</dd>
          <dt>Affected side</dt>
          <dd>{p.affectedSide || "Not given"}</dd>
          <dt>Gender</dt>
          <dd>{p.genderSelfDescribe || p.gender || "Not given"}</dd>
          <dt>Shipping address</dt>
          <dd>{formatAddress(p.address)}</dd>
          <dt>Onboarding</dt>
          <dd>{p.tourCompletedAt ? `Finished ${formatWhen(p.tourCompletedAt)}` : "Not finished"}</dd>
          <dt>Signed up</dt>
          <dd>{patient.account ? formatWhen(patient.account.createdAt) : "No login account"}</dd>
          {patient.account && (
            <>
              <dt>Account status</dt>
              <dd>
                {patient.account.status || "Unknown"}
                {patient.account.enabled ? "" : " (disabled)"}
              </dd>
            </>
          )}
        </dl>
      </section>

      <section className="ui-card">
        <h2 className={styles.heading}>Access</h2>
        <p className={styles.line}>Groups: {patient.groups.length ? patient.groups.join(", ") : "none"}</p>
        <button type="button" className={`ui-btn ui-btn--sm ${isProvider ? "ui-btn--danger" : ""}`} disabled={busy || !patient.account} onClick={toggleProvider}>
          {isProvider ? "Remove Provider" : "Make Provider"}
        </button>
        <p className={styles.help}>Admin access is granted only from CloudShell, never here.</p>

        <h3 className={styles.sub}>Shares data with</h3>
        {patient.sharingWith.length === 0 ? (
          <p className={styles.line}>Nobody.</p>
        ) : (
          <ul className={styles.list}>
            {patient.sharingWith.map((v) => (
              <li key={v}>
                <span>{v}</span>
                <button type="button" className="ui-btn ui-btn--sm ui-btn--ghost" disabled={busy} onClick={() => unshare(v)}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="ui-card">
        <h2 className={styles.heading}>Tags and test account</h2>
        <label className="ui-label" htmlFor="admin-tags">
          Tags (comma separated)
        </label>
        <div className={styles.row}>
          <input id="admin-tags" className="ui-input" value={tagText} onChange={(e) => setTagText(e.target.value)} placeholder="GSU pilot, Advisory board" />
          <button type="button" className="ui-btn ui-btn--sm" disabled={busy} onClick={saveTags}>
            Save tags
          </button>
        </div>
        <label className={styles.check}>
          <input type="checkbox" checked={patient.isTest} disabled={busy} onChange={toggleTest} />
          Test account (hidden from overview counts)
        </label>
      </section>

      <section className={`ui-card ${styles.wide}`}>
        <h2 className={styles.heading}>Recent admin changes</h2>
        {audit.length === 0 ? (
          <p className={styles.line}>None recorded.</p>
        ) : (
          <ul className={styles.list}>
            {audit.map((r) => (
              <li key={`${r.at}-${r.action}`}>
                <span>
                  {formatWhen(r.at)} · {ACTION_LABELS[r.action] || r.action}: {auditDetail(r)}
                </span>
                <span className={styles.help}>
                  by {r.actorName} ({r.loginEmail})
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
